// Configurações globais da aplicação
// Este arquivo centraliza constantes e configurações baseadas em variáveis de ambiente

import { env } from "@/lib/env";

// Site URLs
export const SITE_URL =
  env.NEXT_PUBLIC_LANDING_PAGE_URL || "https://sendbot.co";
export const SITE_NAME = "SendBot";
export const SITE_DESCRIPTION =
  "Plataforma de Chatbots com IA para WhatsApp e Site";

/**
 * Canonical origin hardcoded to the primary domain.
 * Used for SEO tags (canonical, sitemap, robots, structured data, OG)
 * so that all alternate domains (sendbot.com.br, sendbot.eu, sendbot.cc)
 * point search engines to the canonical sendbot.co domain.
 */
export const CANONICAL_ORIGIN = "https://sendbot.co";

// API endpoints
export const API_BASE_URL =
  env.NEXT_PUBLIC_SENDBOT_API_URL || "https://api-v2.sendbot.co";
export const BLOG_URL = "https://blog.sendbot.co";
export const DOCS_URL = "https://docs.sendbot.co";

const normalizeOrigin = (value: string): string | null => {
  const normalizedValue = value.trim();

  if (normalizedValue === "'self'") return normalizedValue;
  if (/^https?:\/\/\*\.[^/]+$/i.test(normalizedValue)) {
    return normalizedValue;
  }

  try {
    return new URL(normalizedValue).origin;
  } catch {
    return null;
  }
};

const dedupe = (values: string[]): string[] => [...new Set(values)];

// Security & CORS
export const getAllowedOrigins = (): string[] => {
  const origins: string[] = ["https://api-v2.sendbot.co", "https://sendbot.co"];

  // Add current deployment URL if different from sendbot.co
  if (
    env.NEXT_PUBLIC_COOLIFY_URL &&
    !env.NEXT_PUBLIC_COOLIFY_URL.includes("sendbot.co")
  ) {
    origins.push(env.NEXT_PUBLIC_COOLIFY_URL);
  }

  // Add additional allowed origins from environment variable
  if (env.NEXT_PUBLIC_ALLOWED_ORIGINS) {
    const extraOrigins = env.NEXT_PUBLIC_ALLOWED_ORIGINS.split(/[,\s]+/).map(
      (origin) => origin.trim(),
    );
    origins.push(...extraOrigins);
  }

  if (env.ALLOWED_ORIGINS) {
    const extraOrigins = env.ALLOWED_ORIGINS.split(/[,\s]+/).map((origin) =>
      origin.trim(),
    );
    origins.push(...extraOrigins);
  }

  // Development origins
  if (env.NODE_ENV === "development") {
    origins.push("http://localhost:3000", "http://localhost:3001");
  }

  return dedupe(
    origins
      .map((origin) => normalizeOrigin(origin))
      .filter((origin): origin is string => origin !== null),
  );
};

/**
 * Get allowed parent origins for frame-ancestors directive
 * This controls which domains can embed our site in an iframe
 */
export const getAllowedParentOrigins = (): string[] => {
  const origins = ["'self'", SITE_URL];

  // Add additional allowed embedding origins from environment variable
  if (env.NEXT_PUBLIC_ALLOWED_PARENT_ORIGINS) {
    const extraOrigins = env.NEXT_PUBLIC_ALLOWED_PARENT_ORIGINS.split(
      /[,\s]+/,
    ).map((origin) => origin.trim());
    origins.push(...extraOrigins);
  }

  return dedupe(
    origins
      .map((origin) => normalizeOrigin(origin))
      .filter((origin): origin is string => origin !== null),
  );
};

/**
 * Generate the frame-ancestors directive for CSP
 * @param addSelf Whether to include 'self' in the directive
 */
export const getFrameAncestorsDirective = (_addSelf = true): string => {
  const origins = getAllowedParentOrigins();
  return `frame-ancestors ${origins.join(" ")}`;
};

// Analytics
export const GA_TRACKING_ID = env.NEXT_PUBLIC_GA_ID;

// Feature flags
export const FEATURES = {
  enableBlog: true,
  enableDocs: true,
  enableAuth: env.NEXT_PUBLIC_ENABLE_AUTH,
  enableAnalytics: !!env.NEXT_PUBLIC_GA_ID,
  enableIframeEmbed: true,
};

// Cache durations (in seconds)
export const CACHE_TIMES = {
  short: 60, // 1 minute
  medium: 300, // 5 minutes
  long: 3600, // 1 hour
  day: 86400, // 24 hours
  week: 604800, // 7 days
};

// Image domains allowed for next/image
export const getImageDomains = (): string[] => {
  const domains = [
    "hebbkx1anhila5yf.public.blob.vercel-storage.com",
    "api-v2.sendbot.co",
    "sendbot.co",
  ];

  // Add current deployment hostname if available
  if (env.NEXT_PUBLIC_COOLIFY_URL) {
    try {
      const hostname = new URL(env.NEXT_PUBLIC_COOLIFY_URL).hostname;
      if (!domains.includes(hostname)) {
        domains.push(hostname);
      }
    } catch (_e) {
      console.warn("Invalid COOLIFY_URL format");
    }
  }

  return domains;
};

const hasGoogleTagConfiguration = [
  env.NEXT_PUBLIC_GTM_ID,
  env.NEXT_PUBLIC_GA_ID,
].some((value) => Boolean(value?.trim()));

const publicApiOrigin = normalizeOrigin(env.NEXT_PUBLIC_SENDBOT_API_URL);

/**
 * Security header configurations
 */
export const SECURITY_CONFIG = {
  // Content Security Policy (CSP) configuration
  csp: {
    // Keep third-party capabilities opt-in. `script-src` itself is completed
    // with a per-request nonce in lib/security.ts.
    scriptSources: dedupe([
      ...(hasGoogleTagConfiguration
        ? ["https://www.googletagmanager.com"]
        : []),
      ...(env.NEXT_PUBLIC_ENABLE_VERCEL_INSIGHTS
        ? ["https://va.vercel-scripts.com"]
        : []),
    ]),

    // The public demo is served from api-v2, while API deployments can use a
    // separately configured public origin.
    frameSources: dedupe([
      "https://www.youtube.com",
      "https://player.vimeo.com",
      "https://api-v2.sendbot.co",
      ...(publicApiOrigin ? [publicApiOrigin] : []),
      ...(hasGoogleTagConfiguration
        ? ["https://www.googletagmanager.com"]
        : []),
    ]),

    // Attribution uses the configured API origin. The two Sendbot API
    // hostnames remain explicit for backwards-compatible public deployments.
    connectSources: dedupe([
      "https://api.sendbot.co",
      "https://api-v2.sendbot.co",
      ...(publicApiOrigin ? [publicApiOrigin] : []),
      ...(hasGoogleTagConfiguration
        ? [
            "https://www.googletagmanager.com",
            "https://www.google-analytics.com",
            "https://region1.google-analytics.com",
            "https://www.googleadservices.com",
            "https://googleads.g.doubleclick.net",
          ]
        : []),
      ...(env.NEXT_PUBLIC_ENABLE_VERCEL_INSIGHTS
        ? ["https://vitals.vercel-insights.com"]
        : []),
      ...(env.NODE_ENV === "development"
        ? ["http://localhost:*", "ws://localhost:*"]
        : []),
    ]),
  },
};

export const getSecurityHeaderOptions = () => ({
  csp: SECURITY_CONFIG.csp,
  frameAncestors: getAllowedParentOrigins(),
});
